import { inject, Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { AddressBookService } from '@maximizer/core/shared/data-access';
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';
import { finalize } from 'rxjs';
import {
  IqBoostConfirmCreateCRMAction,
  OutlookIqBoostStore,
} from '@maximizer/outlook/ai/data-access';
import { TranslateService } from '@ngx-translate/core';
import { removeSurroundingChars } from '@maximizer/shared/util';
import { OutlookAiMarkupUtils } from '../iq-boost/utils/ai-markup-utils';
import { getCRMActionFromVariation } from '../iq-boost/utils/crm-action-helper';
import {
  ENTITY_KEY_PARAM,
  isValidBase64KeyToken,
} from '../iq-boost/utils/entity-key.utils';

const CREATE_CRM_OBJECT_CLASS = 'create-crm-object';

const DATA_ATTRIBUTES = {
  KEY: 'data-key',
  ACTION: 'data-action',
  TITLE: 'data-title',
  DESCRIPTION: 'data-description',
  DATE: 'data-date',
  DISPLAY_TEXT: 'data-display-text',
} as const;

@Pipe({ name: 'outlookCreateCRMObject' })
export class OutlookCreateCRMObjectPipe implements PipeTransform, OnDestroy {
  private readonly store = inject(OutlookIqBoostStore);
  private readonly addressBook = inject(AddressBookService);
  private readonly translate = inject(TranslateService);

  private clickHandler: ((event: MouseEvent) => void) | null = null;

  constructor() {
    this.setupEventHandlers();
  }

  transform(value?: string): string {
    if (!value) return '';

    // Catalog prompts carry no config; undefined means creation stays allowed.
    const createPrivilege =
      this.store.currentPromptSelection()?.config?.maximizer?.priviliges
        ?.create;

    if (createPrivilege !== false) {
      value = OutlookAiMarkupUtils.replaceCRMObjects(value, (actionContent) =>
        this.handleCRMAction(actionContent),
      );
    }
    return value;
  }

  ngOnDestroy() {
    this.removeEventHandlers();
  }

  private setupEventHandlers() {
    this.removeEventHandlers();

    this.clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target?.classList.contains(CREATE_CRM_OBJECT_CLASS)) return;

      event.preventDefault();
      const action = target.getAttribute(DATA_ATTRIBUTES.ACTION);
      const entityKey = target.getAttribute(DATA_ATTRIBUTES.KEY) ?? '';

      if (!action) return;

      const rawDescription =
        target.getAttribute(DATA_ATTRIBUTES.DESCRIPTION) ?? '';
      const crmAction: IqBoostConfirmCreateCRMAction = {
        key: entityKey,
        displayText: target.getAttribute(DATA_ATTRIBUTES.DISPLAY_TEXT) ?? '',
        title: target.getAttribute(DATA_ATTRIBUTES.TITLE) ?? '',
        description: OutlookCreateCRMObjectPipe.stripHtml(rawDescription),
        action: action as MenuOptionName,
        date: target.getAttribute(DATA_ATTRIBUTES.DATE) ?? '',
        openDialog: true,
      };

      this.store.setConfirmCreateCRMAction(crmAction);

      if (entityKey) {
        this.store.setConfirmCreateCRMActionFor('');
        this.store.setConfirmCreateCRMActionForLoading(true);
        this.addressBook
          .getDisplayValue(entityKey)
          .pipe(
            finalize(() =>
              this.store.setConfirmCreateCRMActionForLoading(false),
            ),
          )
          .subscribe((displayValue) =>
            this.store.setConfirmCreateCRMActionFor(displayValue ?? ''),
          );
      }
    };

    document.addEventListener('click', this.clickHandler);
  }

  private removeEventHandlers() {
    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }
  }

  private handleCRMAction(crmActionContent: string): string {
    try {
      const jsonObject =
        OutlookCreateCRMObjectPipe.parseCRMString(crmActionContent);
      const actionType = jsonObject['type']?.toLowerCase() ?? '';
      const matchingAction = getCRMActionFromVariation(actionType);

      if (!matchingAction) return '';

      const displayText = this.translate.instant(
        matchingAction.result.actionTranslationKey,
      );
      const entityKeyParam = jsonObject[ENTITY_KEY_PARAM] ?? '';
      const entityKey = isValidBase64KeyToken(entityKeyParam)
        ? entityKeyParam
        : '';
      const title = jsonObject['title'] ?? '';
      const description = jsonObject['description'] ?? '';
      const date = jsonObject['date'] ?? '';
      const iconClass = `fa-icons icon-${matchingAction.result.icon} icon-16 mb-1 text-bush-green-140`;

      return `
        <button
          kendobutton
          themecolor="secondary"
          class="${CREATE_CRM_OBJECT_CLASS} rounded k-button k-button-md k-rounded-md k-button-solid-secondary k-button-solid leading-[0]"
          ${DATA_ATTRIBUTES.KEY}="${OutlookCreateCRMObjectPipe.escapeAttribute(entityKey)}"
          ${DATA_ATTRIBUTES.ACTION}="${OutlookCreateCRMObjectPipe.escapeAttribute(String(matchingAction.result.action))}"
          ${DATA_ATTRIBUTES.DISPLAY_TEXT}="${OutlookCreateCRMObjectPipe.escapeAttribute(displayText)}"
          ${DATA_ATTRIBUTES.TITLE}="${OutlookCreateCRMObjectPipe.escapeAttribute(title)}"
          ${DATA_ATTRIBUTES.DESCRIPTION}="${OutlookCreateCRMObjectPipe.escapeAttribute(description)}"
          ${DATA_ATTRIBUTES.DATE}="${OutlookCreateCRMObjectPipe.escapeAttribute(date)}"
          title="${OutlookCreateCRMObjectPipe.escapeAttribute(title)}"
        >
          <e class="${iconClass} pointer-events-none"></e>
          <span class="text-bush-green-140 text-sm font-semibold pointer-events-none">${displayText}</span>
        </button>`;
    } catch (error) {
      console.error('Error handling CRM action:', error);
      return '';
    }
  }

  private static parseCRMString(input: string): Record<string, string> {
    let jsonStr = input.trim();
    jsonStr = removeSurroundingChars(jsonStr, '{', '}');
    jsonStr = removeSurroundingChars(jsonStr, '(', ')');
    const properties =
      OutlookCreateCRMObjectPipe.parsePropertiesFromString(jsonStr);
    return OutlookCreateCRMObjectPipe.processProperties(properties);
  }

  private static parsePropertiesFromString(input: string): string[] {
    const properties: string[] = [];
    let currentProperty = '';
    let insideQuotes = false;
    let currentQuoteChar: string | null = null;
    let escapeNext = false;

    for (const char of input) {
      if (escapeNext) {
        currentProperty += char;
        escapeNext = false;
        continue;
      }
      if (char === '\\') {
        escapeNext = true;
        currentProperty += char;
        continue;
      }
      if (char === '"' || char === "'") {
        if (!insideQuotes) {
          insideQuotes = true;
          currentQuoteChar = char;
        } else if (currentQuoteChar === char) {
          insideQuotes = false;
          currentQuoteChar = null;
        }
        currentProperty += char;
        continue;
      }
      if (char === ',' && !insideQuotes) {
        properties.push(currentProperty.trim());
        currentProperty = '';
        continue;
      }
      currentProperty += char;
    }
    if (currentProperty.trim()) properties.push(currentProperty.trim());
    return properties;
  }

  private static processProperties(
    properties: string[],
  ): Record<string, string> {
    const result: Record<string, string> = {};
    for (const prop of properties) {
      const colonIndex = prop.indexOf(':');
      if (colonIndex === -1) continue;
      const key = prop.substring(0, colonIndex).trim();
      let value = prop.substring(colonIndex + 1).trim();
      value = value
        .replaceAll(String.raw`\"`, '"')
        .replaceAll(String.raw`\'`, "'");
      value = removeSurroundingChars(value, '"', '"');
      value = removeSurroundingChars(value, "'", "'");
      result[key] = value;
    }
    return result;
  }

  private static stripHtml(html: string): string {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent ?? '';
  }

  private static escapeAttribute(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}
